import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Cpu, FileDown, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OrnamentDivider } from "@/components/OrnamentDivider";
import { SiteFooter, SiteHeader } from "@/components/site/SiteChrome";
import { PortfolioGrid } from "@/components/site/PortfolioGrid";
import { ArticleCard } from "@/components/site/ArticleCard";
import { AdSlots } from "@/components/site/AdSlot";
import { Recommendations } from "@/components/site/Recommendations";
import { Bloom, Florals } from "@/components/site/Florals";
import { createDemoWedding, errorMessage, setToken } from "@/lib/api";
import { listArticles } from "@/lib/site";
import { useDocumentMeta, useSite } from "@/hooks/useSite";
import { currentLanguage, t } from "@/i18n";

/* The front door. It carries three jobs at once — sell the tool, show the
   owner's other work, and lead into the journal — so the order matters more
   than the decoration: what the visitor came for first, who made it second. */

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1676734627786-a3662ff6a243?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwd2VkZGluZyUyMHJlY2VwdGlvbiUyMHRhYmxlJTIwc2V0dGluZyUyMGl2b3J5JTIwbGluZW58ZW58MHx8fHwxNzg2NjE2NTc5fDA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { icon: Sparkles, title: "home.pillar1.title", body: "home.pillar1.body" },
  { icon: Cpu, title: "home.pillar2.title", body: "home.pillar2.body" },
  { icon: ShieldCheck, title: "home.pillar3.title", body: "home.pillar3.body" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState([]);
  const site = useSite();
  const lang = currentLanguage();

  useDocumentMeta({
    title: site?.seo?.title || t("app.title"),
    description: site?.seo?.description,
    image: site?.seo?.image_url,
    url: typeof window !== "undefined" ? window.location.origin : "",
  });

  useEffect(() => {
    listArticles({ lang, limit: 3 })
      .then((data) => setArticles(data.articles || []))
      .catch(() => setArticles([]));
  }, [lang]);

  const loadDemo = async () => {
    setLoading(true);
    try {
      const payload = await createDemoWedding();
      const weddingId = payload.wedding.id;
      setToken(weddingId, payload.token);
      // The plan comes back already made, so there is nothing to wait for.
      toast.success(t("home.demoReady"), {
        description: `${t("home.demoPassword")} ${payload.password}`,
        duration: 12000,
      });
      navigate(`/w/${weddingId}/admin?tab=plan`);
    } catch (error) {
      toast.error(errorMessage(error, t("home.demoFailed")));
      setLoading(false);
    }
  };

  // /?demo=1 and /demo both land here: a link handed to someone should open the
  // demo, not a page asking them to press one more button
  useEffect(() => {
    if (params.get("demo") === "1" && !loading) loadDemo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hero = site?.hero || {};

  return (
    <div className="min-h-screen">
      <div className="hero-wash relative overflow-hidden">
        <Florals corner="top-left" size={300} className="opacity-60" />
        <Florals corner="bottom-right" size={260} className="opacity-50" />
        <SiteHeader brandName={site?.brand?.name} tagline={site?.brand?.tagline} />

        <div className="relative mx-auto grid max-w-[1180px] grid-cols-1 items-center gap-12 px-6 pb-20 pt-10 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <p className="eyebrow">{hero.eyebrow || t("landing.eyebrow")}</p>
            <h1 className="mt-3 text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {hero.title || t("landing.slogan")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[color:var(--ink-soft)]">
              {hero.subtitle || t("landing.sub")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                onClick={loadDemo}
                disabled={loading}
                data-testid="load-demo-wedding-button"
                className="h-12 rounded-xl px-6 text-[15px]"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                {hero.second_label || t("landing.demo")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="h-12 rounded-xl border-[color:var(--ink)] px-6 text-[15px]"
              >
                <Link to={hero.cta_href || "/create"} data-testid="create-wedding-link">
                  {hero.cta_label || t("landing.create")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <p className="font-num mt-4 text-[11.5px] text-[color:var(--muted-ink)]">
              {t("home.demoMeta")}
            </p>
          </div>

          <div className="relative">
            <div className="surface overflow-hidden rounded-xl">
              <img
                src={HERO_IMAGE}
                alt=""
                className="h-[260px] w-full object-cover sm:h-[320px]"
                loading="lazy"
              />
              <div className="p-6 text-center">
                <Bloom size={26} className="mx-auto" />
                <p className="font-script mt-2 text-[30px] leading-none" style={{ color: "var(--ink)" }}>
                  {t("home.threeClicks")}
                </p>
                <p className="mt-2 text-[12.5px] text-[color:var(--muted-ink)]">{t("home.threeClicksSub")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-[1180px] px-6 py-16">
        <OrnamentDivider label={t("home.how")} />
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {PILLARS.map((pillar) => (
            <article key={pillar.title} className="surface rounded-xl p-6">
              <pillar.icon className="h-5 w-5" style={{ color: "var(--gild)" }} />
              <h2 className="mt-3 text-xl font-semibold leading-tight">{t(pillar.title)}</h2>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[color:var(--ink-soft)]">
                {t(pillar.body)}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="surface rounded-xl p-6">
            <p className="eyebrow">{t("home.result.eyebrow")}</p>
            <h2 className="mt-2 text-2xl font-semibold">{t("home.result.title")}</h2>
            <ul className="mt-4 space-y-2.5 text-[13.5px] text-[color:var(--ink-soft)]">
              <li>· {t("home.result.a")}</li>
              <li>· {t("home.result.b")}</li>
              <li>· {t("home.result.c")}</li>
              <li>· {t("home.result.d")}</li>
            </ul>
          </section>
          <section className="surface flex flex-col justify-between rounded-xl p-6">
            <div>
              <p className="eyebrow">{t("home.print.eyebrow")}</p>
              <h2 className="mt-2 text-2xl font-semibold">{t("home.print.title")}</h2>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[color:var(--ink-soft)]">
                {t("home.print.body")}
              </p>
            </div>
            <div className="mt-6 flex items-center gap-2 text-[13px] text-[color:var(--ink-soft)]">
              <FileDown className="h-4 w-4" style={{ color: "var(--sage-dark)" }} />
              {t("home.print.meta")}
            </div>
          </section>
        </div>

        {(site?.sections || []).length ? (
          <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="free-sections">
            {site.sections.map((section) => (
              <section key={section.id} className="surface rounded-xl p-6">
                {section.title ? (
                  <h2 className="text-2xl font-semibold leading-tight">{section.title}</h2>
                ) : null}
                {section.body ? (
                  <p className="mt-3 whitespace-pre-line text-[13.5px] leading-relaxed text-[color:var(--ink-soft)]">
                    {section.body}
                  </p>
                ) : null}
              </section>
            ))}
          </div>
        ) : null}

        <AdSlots site={site} placement="home_mid" />

        <PortfolioGrid portfolio={site?.portfolio} />

        <Recommendations affiliates={site?.affiliates} />

        {articles.length ? (
          <section className="mt-20" data-testid="latest-articles">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="eyebrow">{site?.blog?.title || t("site.latest")}</p>
                {site?.blog?.intro ? (
                  <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-[color:var(--ink-soft)]">
                    {site.blog.intro}
                  </p>
                ) : null}
              </div>
              <Link to="/blog" className="link-underline text-[13px]">
                {t("site.allArticles")} →
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </section>
        ) : null}
      </main>

      <SiteFooter site={site} />
    </div>
  );
}
